import { CityStateTemplate as GenericCityStateTemplate } from '@ecomm/city-state-components/CityStateTemplate'
import { Benefits } from '@ecomm/city-state-components/lib/Benefits'
import { BreadcrumbsLocations } from '@ecomm/city-state-components/lib/BreadcrumbsLocations'
import { CityDropdown } from '@ecomm/city-state-components/lib/CityDropdown'
import { HeroBanner } from '@ecomm/city-state-components/lib/HeroBanner'
import { LocationMapAndDropdown } from '@ecomm/city-state-components/lib/LocationMapAndDropdown'
import {
  locationDisplayName,
  regionContentId
} from '@ecomm/city-state-data/lib/cityStateRegion'
import { getMapCoordinates } from '@ecomm/city-state-data/utils/cityStateTemplateHelpers'
import {
  convertPathToCityName,
  validateDynamicCityUrl
} from '@ecomm/city-state-data/utils/dynamicCityLinks'
import {
  IMG_7FApwfEA91pNmqu35jzIoy,
  IMG_7e1hT6DJeh1F38xbRkYxY4
} from '@ecomm/contentful-media'
import { contentfulRichTextSchema } from '@ecomm/contentful-schemas'
import { Footer } from '@ecomm/footer-components'
import { Header, useHeaderRedesignQuery } from '@ecomm/header'
import {
  ComparisonTable,
  type ComparisonTableProps,
  ConfidenceBar,
  ExpertSection,
  GuaranteeSection,
  QuoteWizardSection,
  comparisonTableData,
  transformToComparisonTableData
} from '@ecomm/scout'
import {
  type Region,
  getCityPageLink,
  getStatePageLink
} from '@ecomm/seo-locations'
import { AffirmBanner } from '@ecomm/shared-components/lib/AffirmBanner'
import { CountryRedirectModal } from '@ecomm/shared-components/lib/CountryRedirectModal'
import { TrackingProvider } from '@ecomm/tracking/TrackingProvider'
import type { Locale } from '@ecomm/utils/locale/types'
import { SEO } from '@ecomm/utils/seo'
import { type TypeOf, z } from '@simplisafe/ewok'
import classNames from 'classnames'
import * as O from 'fp-ts/lib/Option'
import { lookup } from 'fp-ts/lib/Record'
import { pipe } from 'fp-ts/lib/function'
import type { PageProps } from 'gatsby'
import { Suspense, useMemo } from 'react'

import { bestSystemsRegionalData } from '@ecomm/city-state-data/lib/cityStateRegion'
import type { TemplateCity as City } from '@ecomm/city-state-data/utils/cityStateTemplateHelpers'
import { useNavigate } from '@ecomm/framework/useNavigate'
import { useStaticData } from '../queries/cityState'
import { useHeroBannerQuery } from '../queries/heroBannerQuery'
import useFooterQuery from '../queries/useFooterQuery'

export type PageContext = {
  readonly cityName?: string
  readonly state: string
  readonly stateId?: string
  readonly lat?: number
  readonly lng?: number
  readonly region: Region
  readonly cities: readonly City[]
  readonly locale: Locale
}

const regionDataSchema = z.object({
  benefitsText: z.object({
    text: contentfulRichTextSchema
  })
})
type RegionData = TypeOf<typeof regionDataSchema>

export type Props<T> = Pick<
  PageProps<T, PageContext>,
  'pageContext' | 'params'
> & {
  readonly data: RegionData
}

const CITY_GROUPS = [
  ['A', 'E'],
  ['F', 'J'],
  ['K', 'O'],
  ['P', 'T'],
  ['U', 'Z']
] as const

function Content<T>({
  pageContext: { cityName, state, stateId, cities, lat, lng, region, locale },
  params
}: Props<T>) {
  const navigate = useNavigate()
  const cityNameDynamic = convertPathToCityName(
    pipe(
      O.fromNullable(params),
      O.chain(lookup('citypath')),
      O.getOrElse(() => '')
    )
  )

  const isDynamicCityPage = cityNameDynamic !== ''
  const city = isDynamicCityPage ? cityNameDynamic : cityName
  const isValidDynamicCityUrl = isDynamicCityPage
    ? validateDynamicCityUrl(cities, cityNameDynamic)
    : true
  !isValidDynamicCityUrl && navigate(getStatePageLink(state))
  const humanReadableLocation = locationDisplayName(state, stateId, city)
  const headerData = useHeaderRedesignQuery()
  const footerData = useFooterQuery()

  const heroBannerData = useHeroBannerQuery('637i03f3OnpIsqhv7Ll5vP')
  const {
    benefitsText,
    expertSectionAssetData,
    affirmData,
    confidenceBarData,
    guaranteeAsset,
    guaranteeCopy,
    quoteWizard
  } = useStaticData(regionContentId[region])

  const transformedComparisonTableData: ComparisonTableProps =
    transformToComparisonTableData(comparisonTableData[locale])

  const mapCoordinates = getMapCoordinates(
    cities,
    isDynamicCityPage,
    cityNameDynamic,
    lat,
    lng
  )

  const mapData = useMemo(
    () => ({
      longitude: mapCoordinates.lng ?? 0,
      latitude: mapCoordinates.lat ?? 0,
      zoom: city ? 13 : 8
    }),
    [mapCoordinates.lng, mapCoordinates.lat, city]
  )

  const expertSectionData = useMemo(
    () => ({
      image: expertSectionAssetData
    }),
    [expertSectionAssetData]
  )

  const dropdownRedirectorData = useMemo(
    () =>
      city
        ? null
        : {
            title: 'We’re in your neighborhood',
            subtitle: 'Select your City or Town',
            links: cities
          },
    [city, cities]
  )

  const bestSystemsData = useMemo(
    () => ({
      title: bestSystemsRegionalData.title.replace(
        '{{location}}',
        humanReadableLocation
      ),
      description: bestSystemsRegionalData.description[region].replace(
        '{{location}}',
        humanReadableLocation
      )
    }),
    [humanReadableLocation, region]
  )
  const benefitsImages = useMemo(() => [IMG_7e1hT6DJeh1F38xbRkYxY4], [])

  const memoizedMetaKeywords = useMemo(() => [], [])

  return (
    <TrackingProvider
      metaTitle={`Shop Home Security Systems & Packages for ${humanReadableLocation} | SimpliSafe`}
    >
      <SEO
        canonicalLink={
          cityName ? getCityPageLink(state, cityName) : getStatePageLink(state)
        }
        isLandingPage={true}
        isNofollow={isDynamicCityPage}
        isNoindex={isDynamicCityPage}
        lang="en-US"
        metaDescription={`Browse SimpliSafe home security systems and packages in ${humanReadableLocation}. Get setup in minutes and start protecting your home today.`}
        metaKeywords={memoizedMetaKeywords}
        metaTitle={`Shop Home Security Systems & Packages for ${humanReadableLocation} | SimpliSafe`}
      />
      <GenericCityStateTemplate>
        {{
          Header: <Header {...headerData} />,
          Breadcrumbs: (
            <BreadcrumbsLocations
              city={city}
              isDynamicCityPage={isDynamicCityPage}
              state={state}
            />
          ),
          Content: (
            <main id="content">
              <div className="mx-auto mt-5 max-w-[1372px] lg:-mt-3">
                <HeroBanner
                  heroBannerProps={heroBannerData}
                  location={humanReadableLocation}
                />
              </div>

              <div
                className={classNames(
                  'prose mx-auto flex max-w-8xl flex-col gap-14 whitespace-pre-line px-4 pt-8 md:prose-md lg:prose-lg md:gap-16 md:px-8 lg:gap-20',
                  'mb-10 md:-mt-6 md:mb-16 lg:mb-20'
                )}
              >
                <ConfidenceBar data={confidenceBarData.content} />
                <AffirmBanner data={affirmData} />
              </div>
              {mapCoordinates.lng && mapCoordinates.lat ? (
                <LocationMapAndDropdown
                  bestSystemsData={bestSystemsData}
                  dropdownRedirectorData={dropdownRedirectorData}
                  mapData={mapData}
                />
              ) : null}
              <div
                className={classNames(
                  'prose mx-auto flex max-w-8xl flex-col gap-14 whitespace-pre-line px-4 pt-8 md:prose-md lg:prose-lg md:gap-16 md:px-8 lg:gap-20',
                  'mb-14 mt-8 md:mb-20 md:mt-14 lg:mb-28 lg:mt-20'
                )}
              >
                <QuoteWizardSection
                  hideExtraTextOnMobile={false}
                  image={IMG_7FApwfEA91pNmqu35jzIoy}
                  quoteWizard={quoteWizard.quoteWizard}
                />
                <Benefits
                  images={benefitsImages}
                  location={humanReadableLocation}
                  text={benefitsText}
                />
                <ComparisonTable data={transformedComparisonTableData} />
                <GuaranteeSection
                  copy={guaranteeCopy}
                  image={guaranteeAsset.guaranteeAsset}
                  quoteWizard={quoteWizard.quoteWizard}
                />
                <ExpertSection data={expertSectionData} />
                <CityDropdown
                  cities={cities}
                  groups={CITY_GROUPS}
                  title="Select your City or Town"
                />
              </div>
            </main>
          ),
          Footer: <Footer {...footerData} />,
          Modal: <CountryRedirectModal />
        }}
      </GenericCityStateTemplate>
    </TrackingProvider>
  )
}

export default function CityStateTemplate<T>(props: Props<T>) {
  return (
    <Suspense fallback={null}>
      <Content {...props} />
    </Suspense>
  )
}
